import { POIData } from '../poi/POIData';
import { AppSettingsData } from '../core/AppSettingsData';
import { LayoutData } from '../layout/LayoutData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { Color } from '../types/Color';
import { Vector3 } from '../types/Vector3';
import { Vector2 } from '../types/Vector2';
import { ThemeRegistry } from '../theme/ThemeRegistry';

/**
 * MarkdownParser - Parses markdown content into structured POI data
 * Handles the markdown format with HTML comment metadata
 */
export class MarkdownParser {
    private _appPropertyNames: string[] | null = null;
    private _layoutPropertyNames: string[] | null = null;
    private _scenePropertyNames: string[] | null = null;
    private _poiPropertyNames: string[] | null = null;
    
    /**
     * Parse markdown content and directly update the provided instances
     * @param markdownContent The markdown content to parse
     * @param sceneSettings SceneSettingsData instance to update directly
     * @param appSettings AppSettingsData instance to update directly  
     * @param layoutData LayoutData instance to update directly (optional)
     * @returns Array of POIData instances
     */
    async parse(
        markdownContent: string, 
        sceneSettings?: SceneSettingsData, 
        appSettings?: AppSettingsData,
        layoutData?: LayoutData
    ): Promise<POIData[]> {
        try {
            // Quick debug summary: first H1 and number of POI blocks
            const firstH1Match = markdownContent.match(/^#\s+(.+)$/m);
            const titlePreview = firstH1Match ? firstH1Match[1].trim() : '(no title)';
            console.groupCollapsed('[Parser] parse');
            console.info('title=', titlePreview);
            console.groupEnd();
        } catch {}
        // Check for global header format
        let contentAfterGlobal = markdownContent;
        const globalHeaderRegex = /^---GLOBAL---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
        const globalMatch = markdownContent.match(globalHeaderRegex);
        
        if (globalMatch) {
            // Parse and directly apply global settings
            const globalContent = globalMatch[1];
            contentAfterGlobal = globalMatch[2];
            
            this.parseAndApplyGlobalSettings(globalContent, sceneSettings, appSettings, layoutData);
        }
        
        // Split content into POI blocks with robust regex
        const splitRegex = /(?:^|\r?\n)\s*---\s*(?:\r?\n|$)/;
        const poiBlocks = contentAfterGlobal.split(splitRegex)
            .map(s => s.trim())
            .filter(s => s.length > 0);
        

        
        // Parse each POI block and create POIData instances directly
        const pois: POIData[] = [];
        for (let i = 0; i < poiBlocks.length; i++) {
            const block = poiBlocks[i].trim();
            if (block) {
                const poiDataInstance = this.createPOIDataFromBlock(block, i, sceneSettings, appSettings);
                if (poiDataInstance) {
                    pois.push(poiDataInstance);
                }
            }
        }
        
        return pois;
    }
    
    /**
     * Parse global header content and directly apply to provided instances.
     *
     * Two passes: a `theme:` line expands to its preset values FIRST, then every explicit
     * line of the block is applied on top. A preset therefore never overrides what the
     * author wrote underneath it, regardless of line order.
     */
    private parseAndApplyGlobalSettings(
        globalContent: string,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData,
        layoutData?: LayoutData
    ): void {
        const entries = this.readGlobalEntries(globalContent);

        const themeEntry = entries.find(e => e.key === 'theme');
        if (themeEntry) {
            this.applyTheme(themeEntry.value, sceneSettings, appSettings, layoutData);
        }

        for (const entry of entries) {
            if (entry.key === 'theme') continue;
            this.applyGlobalKey(entry.key, entry.value, sceneSettings, appSettings, layoutData);
        }
    }

    /** `key: value` lines of a GLOBAL block, in order; lines without a key are skipped. */
    private readGlobalEntries(globalContent: string): { key: string; value: string }[] {
        const entries: { key: string; value: string }[] = [];
        for (const line of globalContent.split('\n')) {
            const colonIndex = line.indexOf(':');
            if (colonIndex === -1) continue;
            const key = line.substring(0, colonIndex).trim();
            if (!key) continue;
            entries.push({ key, value: line.substring(colonIndex + 1).trim() });
        }
        return entries;
    }

    /**
     * Expand a theme preset into the instances. Unknown names are reported loudly and
     * recorded verbatim in `appSettings.theme`, so the editor can show "unknown theme"
     * instead of silently falling back to defaults.
     */
    private applyTheme(
        name: string,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData,
        layoutData?: LayoutData
    ): void {
        const preset = ThemeRegistry.Instance.get(name);
        if (!preset) {
            console.warn(`Unknown theme "${name}" - known themes: ${ThemeRegistry.Instance.names().join(', ')}`);
            if (appSettings) this.applyPropertyToInstance(appSettings, 'theme', ThemeRegistry.normalize(name), 'App');
            return;
        }
        for (const [key, value] of Object.entries(preset.values)) {
            this.applyGlobalKey(key, String(value), sceneSettings, appSettings, layoutData);
        }
        if (appSettings) this.applyPropertyToInstance(appSettings, 'theme', preset.name, 'App');
    }

    /** Route one GLOBAL key to layout, app or scene settings (with the historic aliases). */
    private applyGlobalKey(
        key: string,
        value: string,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData,
        layoutData?: LayoutData
    ): void {
        const parsedValue = this.parseValue(value);

        // Determine property type and apply directly to the instance
        if (layoutData && this.isLayoutProperty(key)) {
            // Normalize layouter string like 'track' to enum-aware property
            if (key === 'layouter' && typeof parsedValue === 'string') {
                try {
                    (layoutData as any).layouter = parsedValue;
                } catch {}
            } else {
                this.applyPropertyToInstance(layoutData, key, parsedValue, 'Layout');
            }
        } else if (appSettings && this.isAppSettingProperty(key)) {
            // Back-compat aliases
            if (key === 'labelOrientationMode') {
                // Map orientation alias to billboard boolean
                const normalized = String(value).toLowerCase();
                const billboard = normalized === 'billboard' || normalized === 'true' || normalized === '1';
                this.applyPropertyToInstance(appSettings, 'labelBillboardMode', billboard, 'App');
            } else {
                this.applyPropertyToInstance(appSettings, key, parsedValue, 'App');
            }
        } else if (sceneSettings && this.isSceneSettingProperty(key)) {
            this.applyPropertyToInstance(sceneSettings, key, parsedValue, 'Scene');
        } else {
            // Fallback: if unknown and appSettings exists, put there
            if (appSettings) {
                console.warn(`Unknown property "${key}" - adding to appSettings as fallback`);
                // Back-compat aliases even in fallback path
                if (key === 'labelOrientationMode') {
                    const normalized = String(value).toLowerCase();
                    const billboard = normalized === 'billboard' || normalized === 'true' || normalized === '1';
                    this.applyPropertyToInstance(appSettings, 'labelBillboardMode', billboard, 'App');
                } else {
                    this.applyPropertyToInstance(appSettings, key, parsedValue, 'App');
                }
            }
        }
    }

    /**
     * Apply a property value directly to an instance with type-safe parsing
     */
    private applyPropertyToInstance(instance: any, key: string, value: any, type: string): void {

        const property = (instance as any)[key];
        if (property && typeof property === 'object' && 'value' in property) {
            try {
                // 🔍 Type-Safe Parsing: Check expected type and convert accordingly
                const expectedType = this.getPropertyExpectedType(property);

                const typeSafeValue = this.parseValueWithTypeCheck(value, expectedType, key, type);
                
                if (typeSafeValue !== null) {
                    const oldValue = property.value;
                    property.value = typeSafeValue;

                } else {

                    console.warn(`⚠️ Type mismatch for ${type} property "${key}": expected ${expectedType}, got "${value}" - assignment skipped`);
                }
            } catch (error) {
                console.warn(`❌ Failed to apply ${type} setting ${key}:`, error);
            }
        } else {
            console.warn(`⚠️ Property "${key}" not found on ${type} instance or has no .value setter`);
        }
    }
    
    /**
     * Parse individual POI block using the same generic approach as global settings
     */
    private parsePOIBlock(block: string, index: number): any | null {
        // Extract HTML comment metadata
        const commentRegex = /<!--([\s\S]*?)-->/;
        const match = block.match(commentRegex);
        
        // 🎯 FIXED: Remove hardcoded defaults - let POIData properties define their own defaults
        const metadata: any = {};
        
        if (match) {
            const metaContent = match[1];
            const lines = metaContent.split('\n');
            
            lines.forEach(line => {
                const colonIndex = line.indexOf(':');
                if (colonIndex !== -1) {
                    const key = line.substring(0, colonIndex).trim();
                    const value = line.substring(colonIndex + 1).trim();
                    
                    // 🎯 Store raw string value - type checking happens later in applyPropertyToInstance
                    metadata[key] = value;
                    

                }
            });
        }
        
        // Extract content (remove HTML comment)
        const content = block.replace(/<!--[\s\S]*?-->/, '').trim();
        
        // Extract title (first heading)
        const titleMatch = content.match(/^#\s+(.+)$/m);
        const title = titleMatch ? titleMatch[1] : `POI ${index + 1}`;
        
        // Keep the full markdown content INCLUDING the title
        const mainContent = content;
        
        return {
            name: title,           // Still extract title for POI name
            title: title,          // Also add title property for test compatibility
            markdown: mainContent, // But keep full content in markdown property
            ...metadata,
            index
        };
    }
    
    /**
     * Create POIData instance directly from block using generic property application
     * Now uses the same approach as global settings for consistency
     */
    private createPOIDataFromBlock(block: string, index: number, sceneSettings?: SceneSettingsData, appSettings?: AppSettingsData): POIData | null {
        // First parse the block to get the raw metadata
        const rawData = this.parsePOIBlock(block, index);
        if (!rawData) return null;
        
        // 🎯 AUTO-DETECTION: Detect shape from URI if no shape specified
        this.applyAutoDetection(rawData);
        
        // Create POIData instance with proper inheritance
        const poiData = new POIData(sceneSettings, appSettings);
        
        // 🎯 NEW APPROACH: Use the same generic property application as global settings
        
        for (const [key, value] of Object.entries(rawData)) {
            // Skip metadata fields that are not POI properties
            if (key === 'index') continue;
            
            // 🎯 FIXED: Use generic property validation like global settings
            if (this.isPOIProperty(key)) {

                this.applyPropertyToInstance(poiData, key, value, 'POI');
            } else if (key === 'name' || key === 'title') {
                // Special handling for name/title - these set the markdown content.
                // If the POI block has actual markdown body, use it. Otherwise leave markdown
                // empty so labels with no real content stay invisible (no "POI N" fallback text
                // bleeding into the rendered label).
                try {
                    if (rawData.markdown && String(rawData.markdown).trim().length > 0) {
                        poiData.markdown.value = String(rawData.markdown);
                    }
                    // else: leave markdown.value at its default empty string
                } catch (error) {
                    console.warn(`  ❌ Failed to set markdown content:`, error);
                }
            } else if (key === 'markdown') {
                // Direct markdown content
                try {
                    poiData.markdown.value = String(value);
                    //console.log(`  ✅ Set markdown content directly`);
                } catch (error) {
                    console.warn(`  ❌ Failed to set markdown content:`, error);
                }
            } else {
                //console.log(`  ⏭️ Skipped ${key} (not a POI property)`);
            }
        }
        
        return poiData;
    }
    
    /**
     * Determine the expected type of a property based on its explicit type field
     */
    private getPropertyExpectedType(property: any): string {
        // 🎯 NEW: Use explicit expectedType field if available
        if (property.expectedType && property.expectedType !== 'unknown') {
            return property.expectedType;
        }
        
        // 🔄 FALLBACK: Infer from default value if expectedType not set
        const defaultValue = property._defaultValue || property.getHierarchicalDefaultValue?.();
        
        if (defaultValue instanceof Vector3) return 'Vector3';
        if (defaultValue instanceof Vector2) return 'Vector2';
        if (defaultValue instanceof Color) return 'Color';
        if (Array.isArray(defaultValue)) {
            // Check array element types
            if (defaultValue.length === 0) {
                // Empty array - check property name for hints
                if (property.name && (property.name.toLowerCase().includes('opacity') || property.name === 'planeOpacity')) {
                    return 'number[]';
                }
                return 'string[]'; // Default to string array
            }
            if (defaultValue.every(item => typeof item === 'string')) {
                return 'string[]';
            }
            if (defaultValue.every(item => typeof item === 'number')) {
                return 'number[]';
            }
            return 'array'; // Generic array (lowercase for consistency)
        }
        if (typeof defaultValue === 'number') return 'number';
        if (typeof defaultValue === 'boolean') return 'boolean';
        if (typeof defaultValue === 'string') return 'string';
        
        return 'unknown';
    }
    
    /**
     * Parse value with type checking against expected property type
     */
    private parseValueWithTypeCheck(value: any, expectedType: string, propertyName: string, instanceType: string): any {

        
        // If value is already the correct type, return it
        if (this.isCorrectType(value, expectedType)) {
            return value;
        }
        
        // If value is a string, try to parse it to the expected type
        if (typeof value === 'string') {
            const parsed = this.parseStringToType(value, expectedType);
            if (parsed !== null) {
                //console.log(`🔄 Type conversion for ${instanceType}.${propertyName}: "${value}" → ${expectedType}`);
                return parsed;
            }
        }
        
        // Type mismatch - return null to signal failure
        console.warn(`🚫 Cannot convert "${value}" (${typeof value}) to expected type ${expectedType} for ${instanceType}.${propertyName}`);
        return null;
    }
    
    /**
     * Check if value is already the correct type
     */
    private isCorrectType(value: any, expectedType: string): boolean {
        switch (expectedType) {
            case 'Vector3': return value instanceof Vector3;
            case 'Vector2': return value instanceof Vector2;
            case 'Color': return value instanceof Color;
            case 'number': return typeof value === 'number' && !isNaN(value);
            case 'boolean': return typeof value === 'boolean';
            case 'string': return typeof value === 'string';
            case 'Array<string>':
            case 'string[]': 
                return Array.isArray(value) && value.every(item => typeof item === 'string');
            case 'Array<number>':
            case 'number[]': 
                return Array.isArray(value) && value.every(item => typeof item === 'number' && !isNaN(item));
            case 'array':
            case 'Array': 
                return Array.isArray(value);
            default: return false;
        }
    }
    
    /**
     * Parse string to specific type
     */
    private parseStringToType(value: string, expectedType: string): any {
        switch (expectedType) {
            case 'Vector3':
                return this.parseStringToVector3(value);
            case 'Vector2':
                return this.parseStringToVector2(value);
            case 'Color':
                return this.parseStringToColor(value);
            case 'number':
                const num = parseFloat(value);
                return !isNaN(num) && isFinite(num) ? num : null;
            case 'boolean':
                if (value.toLowerCase() === 'true') return true;
                if (value.toLowerCase() === 'false') return false;
                return null;
            case 'string':
                return value;
            case 'Array<string>':
            case 'string[]':
                // 🎯 Support JSON/array syntax: ["a", "b"] or [a, b]
                if (value.startsWith('[') && value.endsWith(']')) {
                    try {
                        // Try JSON parse first
                        const parsed = JSON.parse(value);
                        if (Array.isArray(parsed)) {
                            return parsed.map((s: any) => String(s).trim()).filter((s: string) => s !== '');
                        }
                    } catch {
                        // Fallback: manual parser for bracketed comma-separated strings (strip quotes if present)
                        const inner = value.slice(1, -1);
                        const parts = inner.split(',').map(s => s.trim().replace(/^['\"]|['\"]$/g, ''));
                        const cleaned = parts.filter(s => s !== '');
                        if (cleaned.length > 0) return cleaned;
                    }
                }
                // 🎯 Parse semicolon-separated URI strings to array (legacy)
                return value.split(';').map(s => s.trim()).filter(s => s !== '');
            case 'Array<number>':
            case 'number[]':
                // 🎯 Parse comma-separated numbers to array (consistent with Vector syntax)
                const numbers = value.split(',')
                    .map(s => parseFloat(s.trim()))
                    .filter(n => !isNaN(n) && isFinite(n));
                return numbers.length > 0 ? numbers : null;
            case 'Array':
            case 'array':
                // 🎯 Generic array - try to detect if it's numbers or strings
                if (value.includes(',')) {
                    // Has commas - try as numbers first
                    const numbers = value.split(',')
                        .map(s => parseFloat(s.trim()))
                        .filter(n => !isNaN(n) && isFinite(n));
                    if (numbers.length > 0) {
                        return numbers;
                    }
                }
                // Fallback to semicolon-separated strings
                return value.split(';').map(s => s.trim()).filter(s => s !== '');
            default:
                return null;
        }
    }
    
    /**
     * Parse string to Vector3 (supports both comma and array syntax)
     */
    private parseStringToVector3(value: string): Vector3 | null {
        // Array syntax: "[0, 8, -8]"
        if (value.startsWith('[') && value.endsWith(']')) {
            try {
                const arrayContent = value.slice(1, -1);
                const parts = arrayContent.split(',').map(s => s.trim());
                if (parts.length === 3) {
                    const nums = parts.map(p => parseFloat(p));
                    if (nums.every(n => !isNaN(n) && isFinite(n))) {
                        return new Vector3(nums[0], nums[1], nums[2]);
                    }
                }
            } catch (error) {
                // Fall through to return null
            }
        }
        
        // Comma syntax: "0.5,0.8,1.2"
        if (value.includes(',')) {
            const parts = value.split(',').map(s => s.trim());
            if (parts.length === 3) {
                const nums = parts.map(p => parseFloat(p));
                if (nums.every(n => !isNaN(n) && isFinite(n))) {
                    return new Vector3(nums[0], nums[1], nums[2]);
                }
            }
        }
        
        return null;
    }
    
    /**
     * Parse string to Vector2 (supports both comma and array syntax)
     */
    private parseStringToVector2(value: string): Vector2 | null {
        // Array syntax: "[0, 8]"
        if (value.startsWith('[') && value.endsWith(']')) {
            try {
                const arrayContent = value.slice(1, -1);
                const parts = arrayContent.split(',').map(s => s.trim());
                if (parts.length === 2) {
                    const nums = parts.map(p => parseFloat(p));
                    if (nums.every(n => !isNaN(n) && isFinite(n))) {
                        return new Vector2(nums[0], nums[1]);
                    }
                }
            } catch (error) {
                // Fall through to return null
            }
        }
        
        // Comma syntax: "0.5,0.8"
        if (value.includes(',')) {
            const parts = value.split(',').map(s => s.trim());
            if (parts.length === 2) {
                const nums = parts.map(p => parseFloat(p));
                if (nums.every(n => !isNaN(n) && isFinite(n))) {
                    return new Vector2(nums[0], nums[1]);
                }
            }
        }
        
        return null;
    }
    
    /**
     * Parse string to Color
     */
    private parseStringToColor(value: string): Color | null {
        if (value.startsWith('#') && (value.length === 4 || value.length === 7 || value.length === 9)) {
            try {
                return new Color(value);
            } catch (error) {
                return null;
            }
        }
        // Support rgb(r,g,b) and rgba(r,g,b,a) formats
        if (value.startsWith('rgb')) {
            try {
                return new Color(value);
            } catch (error) {
                return null;
            }
        }
        return null;
    }

    /**
     * Parse string value to appropriate type (legacy method, now enhanced)
     */
    private parseValue(value: string): any {
        // Try to parse as Vector3 (comma-separated coordinates: "0.5,0.8,1.2")
        if (value.includes(',')) {
            const parts = value.split(',').map(s => s.trim());
            if (parts.length === 3) {
                const nums = parts.map(p => parseFloat(p));
                if (nums.every(n => !isNaN(n) && isFinite(n))) {
                    const vector = new Vector3(nums[0], nums[1], nums[2]);
                    //console.log(`📍 SerializationParser: Parsed comma-separated value "${value}" → Vector3(${vector.x}, ${vector.y}, ${vector.z})`);
                    return vector;
                }
            }
        }
        
        // Try to parse as Vector3 (array syntax: "[0, 8, -8]")
        if (value.startsWith('[') && value.endsWith(']')) {
            try {
                const arrayContent = value.slice(1, -1); // Remove [ and ]
                const parts = arrayContent.split(',').map(s => s.trim());
                if (parts.length === 3) {
                    const nums = parts.map(p => parseFloat(p));
                    if (nums.every(n => !isNaN(n) && isFinite(n))) {
                        const vector = new Vector3(nums[0], nums[1], nums[2]);
                        //console.log(`📍 SerializationParser: Parsed array syntax "${value}" → Vector3(${vector.x}, ${vector.y}, ${vector.z})`);
                        return vector;
                    }
                }
            } catch (error) {
                console.warn(`⚠️ SerializationParser: Failed to parse array syntax "${value}"`);
            }
        }
        
        // Try to parse as Color (hex colors starting with #)
        if (value.startsWith('#') && (value.length === 4 || value.length === 7)) {
            try {
                const color = new Color(value);
                //console.log(`🎨 SerializationParser: Parsed color "${value}" → Color object`);
                return color;
            } catch (error) {
                console.warn(`⚠️ SerializationParser: Failed to parse color "${value}", using as string`);
            }
        }
        
        // Try to parse as number
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && isFinite(numValue)) {
            return numValue;
        }
        
        // Try to parse as boolean
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
        
        // Return as string
        return value;
    }
    
    /**
     * Check if property belongs to layout data
     * 🎯 Uses reflection to get property names from LayoutData class - NO MORE DUPLICATION!
     */
    private isLayoutProperty(key: string): boolean {
        // Accept friendly alias 'layouter' in addition to reflected property names
        if (key === 'layouter') return true;
        return this.getLayoutPropertyNames().includes(key);
    }
    
    /**
     * Get all layout property names via reflection
     */
    private getLayoutPropertyNames(): string[] {
        if (!this._layoutPropertyNames) {
            try {
                // Create temporary instance to extract property names via reflection
                const tempLayout = new LayoutData();
                this._layoutPropertyNames = this.extractPropertyNamesFromObject(tempLayout);
            } catch (error) {
                console.warn('Could not load LayoutData properties via reflection, using fallback');
                this._layoutPropertyNames = ['layouter', 'gridSpacing', 'fieldAwarenessEnabled'];
            }
        }
        return this._layoutPropertyNames;
    }
    
    /**
     * Check if property belongs to app settings
     * 🎯 Uses reflection to get property names from AppSettingsData class
     */
    private isAppSettingProperty(key: string): boolean {
        return this.getAppPropertyNames().includes(key);
    }
    
    /**
     * Get all app property names via reflection
     */
    private getAppPropertyNames(): string[] {
        if (!this._appPropertyNames) {
            try {
                const tempApp = new AppSettingsData();
                // 🎯 Use getAllProperties() method for consistency!
                this._appPropertyNames = tempApp.getAllProperties().map(p => p.name);
            } catch (error) {
                console.warn('Could not load AppSettingsData properties via reflection, using fallback');
                this._appPropertyNames = ['cameraDistance', 'labelColor', 'showConnector'];
            }
        }
        return this._appPropertyNames;
    }
    
    /**
     * Check if property belongs to scene settings
     * 🎯 Uses reflection to get property names from SceneSettingsData class
     */
    private isSceneSettingProperty(key: string): boolean {
        return this.getScenePropertyNames().includes(key);
    }
    
    /**
     * Get all scene property names via reflection
     */
    private getScenePropertyNames(): string[] {
        if (!this._scenePropertyNames) {
            try {
                const tempScene = new SceneSettingsData();
                // 🎯 Use getAllProperties() method for consistency!
                this._scenePropertyNames = tempScene.getAllProperties().map(p => p.name);
            } catch (error) {
                console.warn('Could not load SceneSettingsData properties via reflection, using fallback');
                this._scenePropertyNames = ['primaryColor', 'scale', 'animationSpeed'];
            }
        }
        return this._scenePropertyNames;
    }
    
    /**
     * Check if property belongs to POI data
     * 🎯 Uses reflection to get property names from POIData class
     */
    private isPOIProperty(key: string): boolean {
        return this.getPOIPropertyNames().includes(key);
    }
    
    /**
     * Get all POI property names via reflection
     */
    private getPOIPropertyNames(): string[] {
        if (!this._poiPropertyNames) {
            try {
                const tempPOI = new POIData();
                // 🎯 Use getAllProperties() method for consistency!
                this._poiPropertyNames = tempPOI.getAllProperties().map(p => p.name);
            } catch (error) {
                console.warn('Could not load POIData properties via reflection, using fallback');
                this._poiPropertyNames = ['color', 'scale', 'shape', 'opacity'];
            }
        }
        return this._poiPropertyNames;
    }
    
    /**
     * Extract all SettingsProperty names from an object via reflection
     * Based on DebugSystemManager.extractPropertiesFromObject
     */
    private extractPropertyNamesFromObject(obj: any): string[] {
        const propertyNames: string[] = [];
        
        const findProperties = (currentObj: any, visited = new Set()) => {
            if (!currentObj || visited.has(currentObj)) return;
            visited.add(currentObj);

            for (const key in currentObj) {
                if (currentObj.hasOwnProperty(key)) {
                    const value = currentObj[key];

                    // Check if it's a SettingsProperty instance
                    if (value && typeof value === 'object' && 
                        ('name' in value) && ('value' in value) && 
                        ('group' in value) && typeof value.value !== 'undefined') {
                        propertyNames.push(key);
                    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                        // Recursively search in nested objects
                        findProperties(value, visited);
                    }
                }
            }
        };

        findProperties(obj);
        return propertyNames;
    }
    
    /**
     * 🎯 AUTO-DETECTION: Automatically detect shape and other properties from URI
     * No need to manually specify type or shape for common file formats
     */
    private applyAutoDetection(rawData: any): void {
        const uri = rawData.uri;
        
        // Handle both string and array formats
        let firstUri: string;
        if (typeof uri === 'string') {
            // Legacy format: "front.jpg; back.jpg" or just "single.jpg"
            firstUri = uri.split(';')[0]?.trim();
        } else if (Array.isArray(uri) && uri.length > 0) {
            // New format: ["front.jpg", "back.jpg"]
            firstUri = uri[0];
        } else {
            return; // No valid URI
        }
        
        if (!firstUri) return;
        
        // Normalize: strip leading '@', query params and fragments before extension check
        const normalizedUri = firstUri.trim().replace(/^@+/, '');
        const cleanPath = normalizedUri.split('#')[0].split('?')[0];
        // Extract file extension from cleaned URI
        const extension = cleanPath.split('.').pop()?.toLowerCase();
        if (!extension) return;
        
        const changes: string[] = [];
        
        // Auto-detect shape based on file extension
        if (['glb', 'gltf'].includes(extension)) {
            if (!rawData.shape) {
                rawData.shape = 'gltf';
                changes.push(`Auto-set shape to 'gltf' for 3D model`);
            }
        } else if (['usdz'].includes(extension)) {
            if (!rawData.shape) {
                rawData.shape = 'usdz';
                changes.push(`Auto-set shape to 'usdz' for Apple QuickLook AR model`);
            }
        } 
        // else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension)) {
        //     if (!rawData.shape) {
        //         rawData.shape = 'plane'; // Images typically use plane geometry
        //         changes.push(`Auto-set shape to 'plane' for image`);
        //     }
        // } else if (['mp4', 'webm', 'mov', 'ogv'].includes(extension)) {
        //     if (!rawData.shape) {
        //         rawData.shape = 'plane'; // Videos typically use plane geometry  
        //         changes.push(`Auto-set shape to 'plane' for video`);
        //     }
        // } else if (uri.includes('youtube.com') || uri.includes('youtu.be')) {
        //     if (!rawData.shape) {
        //         rawData.shape = 'plane'; // YouTube embeds use plane
        //         changes.push(`Auto-set shape to 'plane' for YouTube`);
        //     }
        // }
        
        // Log any auto-detections
        if (changes.length > 0) {
            //console.log(`🔍 Auto-detection for URI "${firstUri}": ${changes.join(', ')}`);
        }
    }
} 